"use client";
import { useEffect, useState } from "react";

import Link from "next/link";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

import { Loader } from "../Loader";
import ReactPaginate from "react-paginate";

import { useDispatch } from "react-redux";
import { getSiteInfo, typeCheck } from "@/utils";
import { addCrumb, resetCrumb, useGetBlogListQuery } from "@/store";

export const BlogList = ({ showPagination = true, listCount, taglink = true }) => {
  const t = useTranslations();
  const [page, setPage] = useState(1);
  const [isBlogDataLoading, setIsBlogDataLoading] = useState(false);
  const { type, lang } = getSiteInfo();
  const dispatch = useDispatch();

  const path = usePathname();
  const searchParams = useSearchParams();

  let tag = searchParams.get('tag')?.split(',');

  
  const { data: blogData, isLoading, isError } = useGetBlogListQuery({ page: page, tags: tag });

  useEffect(() => {
    if (blogData) {
      setIsBlogDataLoading(true);
      const timer = setTimeout(() => {
        setIsBlogDataLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [blogData]); 

  useEffect(() => {
    const handlePageChange = async (page) => {
      // await getBlogList({ page });
    };
    handlePageChange(page);
  }, [page, path, lang]);
  
  const { meta } = blogData?.body ?? {};
  const totalPages = meta?.last_page;

  const handlePageClick = (event) => {
    let selected = event.selected;
    setPage(selected + 1);
    window.scrollTo({
      top: 300,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (window.location.pathname == '/') return
    const pathName = window.location.pathname.split("/").slice(1)
    const text = pathName?.map((item, i, arr) => ({ text: item[0].toUpperCase() + item.slice(1), slug: item }))
    dispatch(addCrumb(text));
    () => dispatch(resetCrumb())
  }, [dispatch])

  const itemCount = listCount || (blogData?.body?.data?.length || 0);
  if (!blogData?.body?.data && !listCount) {
    return <Loader fullscreen={false} />;
  }


  const dynamicSlug = typeCheck(type, [`/bloq`, `/blog`]);
  
  return (
    <>
      {blogData?.body?.data?.length > 0 && tag && tag?.length > 0 ? (
        <>
          <h1 className="mb-8 text-xl sm:text-center md:text-3xl font-medium text-[#300B60] p-0 -mt-10">
              <span className="w-fit bg-mainLightPurple px-4 rounded-lg py-2 text-white">
                #{tag}
              </span>
          </h1>
          </>
        ) :
          null
      }
      {!blogData?.body?.data?.length ? (
        <>
          <h1 className="mb-4 text-xl sm:text-center md:text-3xl font-medium text-[#300B60] p-0 -mt-10">
              {t("tagNotFound")}
          </h1>
          </>
        ) :
          null
      }
      {(isLoading || isBlogDataLoading) ? (
          <Loader fullscreen={false} />
        ) : (
          <>
        <div className="mb-24 md:p-4 p-2 mx-auto max-w-[1296px] h-full grid md:grid-cols-1 lmd:grid-cols-2 xl:grid-cols-3 gap-6">

          {!!blogData &&
            blogData?.body?.data?.slice(0, itemCount)?.map(({ id, image, created_at, title, tags, slug }) => (

              <Link
                key={id}
                prefetch={true}
                replace={true}
                href={`${dynamicSlug}/${slug}`}
                className="blog-post bg-mainBgPurple relative py-2 md:min-w-[414px] px-2  rounded-[7px] transform hover:shadow-lg hover:border-mainLightPurpleActive transition duration-200 ease-in border-[2px] border-solid border-mainLightPurple cursor-pointer"
              >
                <div>
                  {image ? (
                    <Image
                      className="w-full h-full  object-contain aspect-[16/9]   rounded-[7px] border border-solid border-slate-200"
                      src={`${process.env.API_URL}/storage/${image}`}
                      width={1296}
                      height={256}
                      alt={title}
                    />
                  ) : (
                    ""
                  )}
                </div>
                <h2 className="text-xl font-medium mt-[30px] mb-[24px] text-slate-700 min-h-[56px] line-clamp-2">
                  {title}
                </h2>
                <div className="flex flex-wrap gap-[8px] text-base min-h-[34px] self-end">
                  <span className="text-white cursor-default border rounded-[8px] leading-[24px] bg-auto flex justify-center items-center bg-mainLightPurple px-4 h-[32px]">
                    {created_at}
                  </span>
                  {tags?.map((item, index) => (
                    item && <Link replace href={taglink ? `?tag=${item.name}` : `${dynamicSlug}?tag=${item.name}`}
                      key={index}
                      className="text-white border rounded-[8px] leading-[24px] bg-auto flex justify-center items-center bg-mainLightPurple px-4 h-[32px]"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </Link>
            ))
          }

        </div>
        {showPagination && totalPages > 1 ? (
            <div className="mt-[4rem] mb-[3rem] flex items-center justify-center">
              <ReactPaginate
                breakLabel="..."
                className="flex"
                breakClassName="text-black p-2 self-end"
                pageLinkClassName="w-full h-full grid place-items-center rounded-lg"
                activeLinkClassName="text-black bg-mainLightPurple hover:bg-mainLightPurpleHover active:bg-mainLightPurpleActive text-white rounded-lg"
                pageClassName="border border-grey-200 w-10 h-10 rounded-lg text-slate-400 grid bg-white place-items-center m-1 hover:bg-mainLightPurpleHover hover:text-white active:bg-mainLightPurpleActive transition"
                nextLabel=">"
                nextClassName="border border-grey-200 w-10 h-10 rounded-lg text-slate-400 grid bg-white hover:bg-mainLightPurpleHover hover:text-white active:bg-mainLightPurpleActive place-items-center m-1"
                onPageChange={handlePageClick}
                pageCount={totalPages}
                pageRangeDisplayed={4}
                previousLabel="<"
                renderOnZeroPageCount={null}
                previousClassName="border border-grey-200 w-10 h-10 rounded-lg text-slate-400 grid bg-white hover:bg-mainLightPurpleHover hover:text-white active:bg-mainLightPurpleActive place-items-center m-1"
                previousLinkClassName="w-full h-full grid place-items-center"
                nextLinkClassName="w-full h-full grid place-items-center"
              />
            </div>
          ) : null}

          {tag && tag?.length > 0 ? (
            <div className="mx-auto w-1/2 xmd:w-1/4 mb-20 text-center ">
                  <Link href={typeCheck(type, [`/bloq`, `/blog`])}>
                    <button
                      type="button"
                      className="pt-[.56rem] pb-[.56rem] text-white bg-mainLightPurple hover:bg-mainLightPurpleHover active:bg-mainLightPurpleActive text-center rounded-[8px] btnLspace w-full font-semibold opacity-100"
                    >
                      {t("blog_all")} 
                    </button>
                  </Link>
            </div>
          ) :  null
          }
          </>
      )}


      
    </>
  );
};
